/**
 * Quotes admin catalog — financing plans + utility rebate sources, pulled over
 * from Seth's proposal app (FINANCING_OPTIONS + UTILITY_PROVIDERS) and made
 * admin-editable in Settings → Quotes. Both tables are Pegasus-native + audited.
 * The quote builder reads these to offer a financing plan (est. monthly) and a
 * utility rebate on each Best/Better/Good option.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface FinancingPlan {
  id: string;
  name: string;
  apr: number;
  term_months: number;
  min_amount: number;
  plan_number: string | null;
  description: string | null;
  is_active: boolean;
  sort: number;
  is_special: boolean;
  special_start: string | null;
  special_end: string | null;
  special_description: string | null;
}

export interface Utility {
  id: string;
  name: string;
  default_rebate: number | null;
  is_active: boolean;
  sort: number;
  /** REBATE-TEST-01: what it supplies — a rebate from one that supplies electric on a job with an AC / heat pump = a cooling rebate. */
  provides: "electric" | "gas" | "both";
}

/** Estimated monthly payment for a principal under a plan (amortized; 0% = straight-line). */
export function estMonthly(principal: number, plan: Pick<FinancingPlan, "apr" | "term_months">): number {
  const n = plan.term_months;
  if (!n || principal <= 0) return 0;
  const r = (plan.apr ?? 0) / 100 / 12;
  if (r === 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
}

// ---- Financing plans ----

export function useFinancingPlans(includeInactive = false) {
  return useQuery({
    queryKey: ["financing_plans", includeInactive],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<FinancingPlan[]> => {
      let q = supabase.from("pegasus_financing_plan").select("*").order("sort").order("name");
      if (!includeInactive) q = q.eq("is_active", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as FinancingPlan[];
    },
  });
}

export function useSaveFinancingPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<FinancingPlan> & { name: string }) => {
      const { id, ...rest } = v;
      const payload = {
        name: rest.name,
        apr: rest.apr ?? 0,
        term_months: rest.term_months ?? 0,
        min_amount: rest.min_amount ?? 0,
        plan_number: rest.plan_number ?? null,
        description: rest.description ?? null,
        is_active: rest.is_active ?? true,
        sort: rest.sort ?? 0,
        is_special: rest.is_special ?? false,
        special_start: rest.special_start || null,
        special_end: rest.special_end || null,
        special_description: rest.special_description ?? null,
        updated_at: new Date().toISOString(),
      };
      const { error } = id
        ? await supabase.from("pegasus_financing_plan").update(payload).eq("id", id)
        : await supabase.from("pegasus_financing_plan").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["financing_plans"] }),
  });
}

// ---- Utility rebate sources ----

export function useUtilities(includeInactive = false) {
  return useQuery({
    queryKey: ["utilities", includeInactive],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<Utility[]> => {
      let q = supabase.from("pegasus_utility").select("*").order("sort").order("name");
      if (!includeInactive) q = q.eq("is_active", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Utility[];
    },
  });
}

export function useSaveUtility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<Utility> & { name: string }) => {
      const { id, ...rest } = v;
      const payload = {
        name: rest.name,
        default_rebate: rest.default_rebate ?? null,
        is_active: rest.is_active ?? true,
        sort: rest.sort ?? 0,
        provides: rest.provides ?? "both",
        updated_at: new Date().toISOString(),
      };
      const { error } = id
        ? await supabase.from("pegasus_utility").update(payload).eq("id", id)
        : await supabase.from("pegasus_utility").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["utilities"] });
      void qc.invalidateQueries({ queryKey: ["rebate_test_status"] });
    },
  });
}

// ---- Manufacturer brands (toggle on each Best/Better/Good option) ----

export interface QuoteBrand {
  id: string;
  name: string;
  sort: number;
  is_active: boolean;
}

export function useQuoteBrands(includeInactive = false) {
  return useQuery({
    queryKey: ["quote_brands", includeInactive],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<QuoteBrand[]> => {
      let q = supabase.from("pegasus_quote_brand").select("*").order("sort").order("name");
      if (!includeInactive) q = q.eq("is_active", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as QuoteBrand[];
    },
  });
}

export function useSaveQuoteBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<QuoteBrand> & { name: string }) => {
      const { id, ...rest } = v;
      if (id) {
        // Partial update — don't reset sort/active when only the name changes.
        const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
        if ("name" in rest) patch.name = rest.name;
        if ("sort" in rest) patch.sort = rest.sort;
        if ("is_active" in rest) patch.is_active = rest.is_active;
        const { error } = await supabase.from("pegasus_quote_brand").update(patch).eq("id", id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("pegasus_quote_brand").insert({
          name: rest.name, sort: rest.sort ?? 0, is_active: rest.is_active ?? true,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_brands"] }),
  });
}

// ---- Quote categories (the builder's slots: Furnace / AC / Mini Split / Water Heater /
// Service Repair / Accessories / Enhancements — each maps to a pricebook equip_type) ----

/** A category's TYPE — drives how the builder renders the slot. */
export const CATEGORY_TYPES = ["equipment", "accessory", "enhancement"] as const;
export const CATEGORY_TYPE_LABEL: Record<string, string> = {
  equipment: "Equipment", accessory: "Accessory", enhancement: "Enhancement",
};
/** Which quote type(s) a category shows up on. */
export const APPLIES_TO = ["install", "service", "both"] as const;
export const APPLIES_TO_LABEL: Record<string, string> = {
  install: "Install only", service: "Service only", both: "Both",
};

export interface QuoteCategory {
  id: string;
  key: string;
  label: string;
  parent_key: string | null; // null = top-level slot; else a sub-category card under that slot
  pricebook_category: string | null; // the item.equip_type this slot pulls from
  pricebook_subcategory: string | null; // the match value for a sub-card (subcategory OR category — see pricebook_match)
  pricebook_match: string; // which item column a sub-card filters: "subcategory" (accessories) | "category" (repair components)
  kind: string; // category TYPE: equipment | accessory | enhancement
  icon: string | null;
  sort: number;
  is_active: boolean;
  applies_to: string; // install | service | both
  default_on: boolean;
}

export function useQuoteCategories(includeInactive = false) {
  return useQuery({
    queryKey: ["quote_categories", includeInactive],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<QuoteCategory[]> => {
      let q = supabase.from("pegasus_quote_category").select("*").order("sort").order("label");
      if (!includeInactive) q = q.eq("is_active", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as QuoteCategory[];
    },
  });
}

/** Turn a label into a stable key for a new category ("Garage Heater" → "garage_heater"). */
function slugifyKey(label: string): string {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || `cat_${Date.now()}`;
}

const CATEGORY_FIELDS = [
  "label", "parent_key", "pricebook_category", "pricebook_subcategory", "pricebook_match",
  "kind", "icon", "sort", "is_active", "applies_to", "default_on",
] as const;

// ---- Quote packages (QUOTE-PKG-01) — the office defines a fix/collection once;
// every builder (phone repair, phone replace, desktop) fills an option from it
// in one tap. Lines snapshot pricebook items like pegasus_quote_line does. ----

export interface QuotePackageLine {
  id?: string;
  item_id: string | null;
  kind: string;
  description: string;
  model: string | null;
  manufacturer: string | null;
  image_url: string | null;
  qty: number;
  unit_price: number;
  /** required (locked in) | recommended | optional — same axes as quote lines. */
  role: string;
  /** Included = $0 to the customer, value struck. */
  inc: boolean;
  warranty: string | null;
  sort: number;
}
export interface QuotePackage {
  id: string;
  label: string;
  kind: "repair" | "replace" | "both";
  /** Which collection a replace package aims at (good/better/best) — the
   *  office builder's shelf fills the MATCHING collection first. Null = any. */
  tier: string | null;
  blurb: string | null;
  sort: number;
  is_active: boolean;
  lines: QuotePackageLine[];
}

export function pkgTotal(p: Pick<QuotePackage, "lines">): number {
  return p.lines.reduce((s, l) => s + (l.inc ? 0 : (Number(l.qty) || 0) * (Number(l.unit_price) || 0)), 0);
}

export function useQuotePackages(kind?: "repair" | "replace", includeInactive = false) {
  return useQuery({
    queryKey: ["quote_packages", kind ?? "all", includeInactive],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<QuotePackage[]> => {
      let q = supabase.from("pegasus_quote_package").select("*").order("sort").order("label");
      if (!includeInactive) q = q.eq("is_active", true);
      const { data: pkgs, error } = await q;
      if (error) throw error;
      const rows = (pkgs ?? []) as Omit<QuotePackage, "lines">[];
      const wanted = kind ? rows.filter((p) => p.kind === kind || p.kind === "both") : rows;
      const ids = wanted.map((p) => p.id);
      let lines: QuotePackageLine[] & { package_id?: string }[] = [];
      if (ids.length) {
        const { data: ls, error: le } = await supabase
          .from("pegasus_quote_package_line")
          .select("*")
          .in("package_id", ids)
          .order("sort");
        if (le) throw le;
        lines = (ls ?? []) as (QuotePackageLine & { package_id: string })[];
      }
      return wanted.map((p) => ({
        ...p,
        lines: (lines as (QuotePackageLine & { package_id: string })[]).filter((l) => l.package_id === p.id),
      }));
    },
  });
}

/** Save header + REPLACE the whole line set (simple, atomic enough for a catalog). */
export function useSaveQuotePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<Omit<QuotePackage, "lines">> & { label: string; lines: QuotePackageLine[] }) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const now = new Date().toISOString();
      let id = v.id ?? null;
      if (id) {
        const { error } = await supabase
          .from("pegasus_quote_package")
          .update({ label: v.label, kind: v.kind ?? "repair", tier: v.tier ?? null, blurb: v.blurb ?? null, sort: v.sort ?? 0, is_active: v.is_active ?? true, updated_by: email, updated_at: now })
          .eq("id", id);
        if (error) throw error;
        const { error: de } = await supabase.from("pegasus_quote_package_line").delete().eq("package_id", id);
        if (de) throw de;
      } else {
        const { data, error } = await supabase
          .from("pegasus_quote_package")
          .insert({ label: v.label, kind: v.kind ?? "repair", tier: v.tier ?? null, blurb: v.blurb ?? null, sort: v.sort ?? 0, is_active: v.is_active ?? true, created_by: email })
          .select("id")
          .single();
        if (error) throw error;
        id = (data as { id: string }).id;
      }
      if (v.lines.length) {
        const { error: le } = await supabase.from("pegasus_quote_package_line").insert(
          v.lines.map((l, i) => ({
            package_id: id,
            item_id: l.item_id,
            kind: l.kind || "custom",
            description: l.description,
            model: l.model,
            manufacturer: l.manufacturer,
            image_url: l.image_url,
            qty: l.qty || 1,
            unit_price: l.unit_price || 0,
            role: l.role || "required",
            inc: l.inc ?? false,
            warranty: l.warranty,
            sort: i,
          })),
        );
        if (le) throw le;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_packages"] }),
  });
}

export function useDeleteQuotePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_quote_package").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_packages"] }),
  });
}

export function useSaveQuoteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<QuoteCategory> & { label: string }) => {
      const { id, ...rest } = v;
      if (id) {
        // Partial update — only patch the fields actually provided (never null untouched columns).
        const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
        for (const k of CATEGORY_FIELDS) if (k in rest) patch[k] = (rest as Record<string, unknown>)[k];
        const { error } = await supabase.from("pegasus_quote_category").update(patch).eq("id", id);
        if (error) throw error;
      } else {
        const payload: Record<string, unknown> = {
          label: rest.label,
          parent_key: rest.parent_key ?? null,
          pricebook_category: rest.pricebook_category ?? null,
          pricebook_subcategory: rest.pricebook_subcategory ?? null,
          kind: rest.kind ?? "equipment",
          icon: rest.icon ?? null,
          sort: rest.sort ?? 0,
          is_active: rest.is_active ?? true,
          applies_to: rest.applies_to ?? "install",
          default_on: rest.default_on ?? true,
          key: rest.key && rest.key.trim() ? rest.key.trim() : slugifyKey(rest.label),
        };
        const { error } = await supabase.from("pegasus_quote_category").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_categories"] }),
  });
}
